import { query } from "@solidjs/router";
/* prettier-ignore */
import { GetObjectCommand, ListObjectsV2Command, PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { Readable } from "stream";

import { REGION } from "./constants";
import { authoriseLogbook, logbookAvailable, refuseOverRpc } from "./logbookAccess";

export const BUCKET = "rias-regs";

export const getFolderContents = query(async currentPrefix => {
	"use server";

	// This is callable by anyone, logged in or not, so the caller and the folder
	// are checked here rather than trusting that the page only asks for their own.
	const allowed = await authoriseLogbook( currentPrefix, "read" );

	if ( !allowed.ok ) {
		console.log( "Folder listing refused:", allowed.reason, currentPrefix );

		return { folders: [], files: [] };
	}

	try {
		const s3 = new S3Client( { region: REGION } );

		const command = new ListObjectsV2Command( {
			Bucket: BUCKET,
			Prefix: currentPrefix,
			Delimiter: "/", // ensures we get "folders"
		} );
		const res = await s3.send( command );

		return {
			folders: res.CommonPrefixes?.map( p => p.Prefix ) || [],
			files: res.Contents?.filter( c => c.Key !== currentPrefix ).map( c => ( {
				key: c.Key,
				size: c.Size,
				modified: c.LastModified,
			} ) ),
		};
	} catch ( err ) {
		console.log( "Error getting folder contents for prefix:", currentPrefix, err );
		return { folders: [], files: [] };
	}
}, "getFolderContents");

// The write itself, with no checks: callers decide whether the write is allowed.
const putObject = async ( key: string, buffer: Buffer|string|undefined, contentType: string | undefined = undefined ) => {
    const s3 = new S3Client({ region: REGION });

    const command = new PutObjectCommand({
        Bucket: BUCKET,
        Key: key,
        Body: buffer, // Accepts Node.js Buffer directly
        ContentType: contentType, 
    });

    const res = await s3.send(command);

    return { 
        success: true, 
        etag: res.ETag, // Useful if you want to verify the upload integrity
    };
};

export const uploadFile = async ( key: string, buffer: Buffer|string|undefined, contentType: string | undefined = undefined ) => {
    "use server";

    // Only server code (the BSD export) calls this. Over /_server it would let
    // anyone write anything to the bucket.
    refuseOverRpc();

    return await putObject( key, buffer, contentType );
};

export const downloadFile = async (key: string) => {
	"use server";

	// Nothing in the app calls this from a browser; it must not be reachable as one.
	refuseOverRpc();

	const s3 = new S3Client({ region: REGION });

	const command = new GetObjectCommand({ Bucket: BUCKET, Key: key });

	const res = await s3.send( command );

	const bodyStream = res.Body as Readable;

	// Collect stream into a buffer
	const chunks: Buffer[] = [];
	for await (const chunk of bodyStream) {
		chunks.push(chunk as Buffer);
	}

	const buffer = Buffer.concat(chunks);

	return { buffer, contentType: res.ContentType };
};

export const createS3Folder = async ( key: string ) => {
	"use server";

	const allowed = await authoriseLogbook( key, "write" );

	if ( !allowed.ok ) throw new Error( allowed.reason );

	// A zero-byte object creates just the "folder"
	await putObject( key, "" );
};

// Whether to offer the logbook at all: it is for members who have paid.
export const getLogbookAccess = query( async () => {
	"use server";

	return await logbookAvailable();
}, "logbookAccess" );

/*	const s3 = new S3Client({ region: REGION });

	await s3.send(
		new PutObjectCommand({
			Bucket: BUCKET,
			Key: key,
			Body: "", // zero-byte object
		})
	);
};
*/